import React, { useEffect, useState } from "react";
import LineLoader from "../../../components/Loaders/LineLoader/LineLoader";
import FormLayout from "../../../components/FormLayout";
import { useDispatch, useSelector } from "react-redux";
import { stateSlice } from "../../../store/reducer/state";
import { paymentSlice } from "../../../store/reducer/payment";
import { TaxDocumentStatus } from "../../../enums/tax";
import { useTransitionStateContext } from "../TransitionContainer";
import { Checkpoint } from "../../../enums/checkpoint";
import { RootState } from "../../../store/index";

const PostSignatureTransition: React.FC = () => {
  const dispatch = useDispatch();
  const { taxCalculationsFetched } = useSelector((state: RootState) => state.payment);
  const [userLikelyLoggedBackIn, setUserLikelyLoggedBackIn] = useState(false);

  const {
    isLoading,
    checkpoint,
    taxReturnCompleted,
    taxReturnNeedsPayment,
    taxReturnNeedsRefund
  } = useTransitionStateContext();

  useEffect(() => {
    // If the frontend set a checkpoint, then we know that we
    // have the final balance data available to make a determination
    // whether the user needs to make a payment or accept a refund.
    if (!isLoading && checkpoint === Checkpoint.SignedReturn && taxCalculationsFetched) {
      if (taxReturnNeedsPayment) {
        dispatch(stateSlice.actions.sendToRouteWithDelay("/payment/balance"));
      } else if (taxReturnNeedsRefund) {
        dispatch(stateSlice.actions.sendToRouteWithDelay("/payment/refund"));
      } else if (taxReturnCompleted && userLikelyLoggedBackIn) {
        // Handle user who logged back in with a completed tax return
        dispatch(stateSlice.actions.sendToRouteWithDelay("/user/welcome-back"));
      } else if (taxReturnCompleted) {
        // Handle user in active session with a completed tax return
        dispatch(stateSlice.actions.sendToRouteWithDelay("/payment/complete"));
      } else {
        dispatch(stateSlice.actions.sendToRouteWithDelay("/transition/post-signature-error"));
      }
    }
  }, [isLoading, checkpoint, taxCalculationsFetched]);

  useEffect(() => {
    let ignore = false;

    // If tax calculations do not exist, fetch from backend so we can make determination
    // whether or not to send them to payment-related flows.
    // Likely won't hit this outside of edge case of user with signed return and no payment signing back into Lime
    if (!ignore && !taxCalculationsFetched) {
      setUserLikelyLoggedBackIn(true);
      dispatch(paymentSlice.actions.finalBalanceRequest());
    }

    // If no checkpoint was set, then poll for the expected state to see if backend matches
    if (!ignore && checkpoint !== Checkpoint.SignedReturn) {
      dispatch(stateSlice.actions.pollBackendStateChange(TaxDocumentStatus.Complete));
    }

    return () => {
      ignore = true;
    };
  }, []);

  return (
      <FormLayout
        title={"Checking your filing"}
        subtitle={"Tax Return Status Check"}
        description={userLikelyLoggedBackIn ?
          "Welcome back. Please wait a moment while we check your tax return status." :
          "Please wait a moment while we cross the T's and dot the I's on your return."
        }
      >
        <LineLoader />
      </FormLayout>
  );
};

export default PostSignatureTransition;
